import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import { Loader2, CheckCircle2, XCircle } from 'lucide-react'
import useUploadFile from '@/hooks/useUploadFile'
import {
    createSupplier,
    updateSupplier,
    checkSupplierTaxCode,
} from '@/modules/Contract/modules/Partner/services'
import type {
    Supplier,
    SupplierCreateParams,
} from '@/modules/Contract/modules/Partner/types'
import { STORGE_PREFIXS } from '@/modules/Contract/modules/Process/constants'
import { Attachment } from '@/modules/Upload/types'
import { ModalProps } from '@/types/shared'
import { schemaCreateSupplierModal } from './schema'
import { isAttachment, renderFileItem } from '@/utils/function'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

export interface SupplierModalProps extends ModalProps {
    supplier?: Supplier
    mode?: 'create' | 'update'
    onCreated?: (supplier: Supplier) => void
}

const SupplierModal = ({
    onClose,
    open,
    supplier,
    mode = 'create',
    onCreated,
}: SupplierModalProps) => {
    const { t } = useTranslation('contract')
    const queryClient = useQueryClient()

    // Code validation state
    const [isCheckingCode, setIsCheckingCode] = useState(false)
    const [codeExists, setCodeExists] = useState<boolean | null>(null)

    const {
        control,
        handleSubmit,
        getValues,
        setValue,
        setError,
        clearErrors,
        formState: { errors },
    } = useForm<SupplierCreateParams & { attachments: File[] | Attachment[] }>({
        defaultValues: {
            customer_id: supplier?.customer_id || null,
            customer_contact_id: supplier?.customer_contact_id || null,
            partner_name: supplier?.partner_name || '',
            partner_code: supplier?.partner_code || '',
            partner_email: supplier?.partner_email || '',
            partner_phone: supplier?.partner_phone || '',
            partner_address: supplier?.partner_address || '',
            partner_tax_code: supplier?.partner_tax_code || '',
            partner_representative: supplier?.partner_representative || '',
            partner_representative_phone:
                supplier?.partner_representative_phone || '',
            partner_bank_account: supplier?.partner_bank_account || '',
            partner_bank_name: supplier?.partner_bank_name || '',
            partner_note: supplier?.partner_note || '',
            partner_status: supplier?.partner_status || 'active',
            attachments: supplier?.attachments || [],
        },
        resolver: yupResolver(schemaCreateSupplierModal as any),
    })

    // Check partner tax code on blur
    const handleCheckTaxCode = useCallback(async () => {
        const taxCode = getValues('partner_tax_code')
        if (!taxCode?.trim()) {
            setCodeExists(null)
            return
        }

        // Don't check if it's the same as original (in update mode)
        if (mode === 'update' && taxCode === supplier?.partner_tax_code) {
            setCodeExists(false)
            clearErrors('partner_tax_code')
            return
        }

        setIsCheckingCode(true)
        try {
            const result = await checkSupplierTaxCode({
                partner_tax_code: taxCode,
                partner_id:
                    mode === 'update' ? supplier?.partner_id : undefined,
            })
            setCodeExists(result.exists)
            if (result.exists) {
                setError('partner_tax_code', {
                    type: 'manual',
                    message: t('partner-tax-code-already-exists'),
                })
            } else {
                clearErrors('partner_tax_code')
            }
        } catch (error) {
            console.error('Error checking partner tax code:', error)
            setCodeExists(null)
        } finally {
            setIsCheckingCode(false)
        }
    }, [
        getValues,
        mode,
        supplier?.partner_tax_code,
        supplier?.partner_id,
        setError,
        clearErrors,
        t,
    ])

    const { mutateAsync: create, isPending } = useMutationWithHandler(
        { mutationFn: createSupplier },
        {
            reactQueryKeys: QUERY_KEYS.SUPPLIERS,
            successMessage: t('create-supplier-success'),
            onSuccess: () => {
                onClose()
                queryClient.refetchQueries({
                    queryKey: [QUERY_KEYS.SUPPLIERS],
                })
                queryClient.refetchQueries({
                    queryKey: [QUERY_KEYS.SUPPLIERS, 'options'],
                })
            },
        }
    )

    const { mutateAsync: update, isPending: updatePending } =
        useMutationWithHandler(
            { mutationFn: updateSupplier },
            {
                reactQueryKeys: QUERY_KEYS.SUPPLIERS,
                successMessage: t('update-supplier-success'),
                onSuccess: () => {
                    onClose()
                    queryClient.refetchQueries({
                        queryKey: [QUERY_KEYS.SUPPLIERS],
                    })
                },
            }
        )

    const { uploadAsync } = useUploadFile({
        prefix: STORGE_PREFIXS.PARTNER,
    })

    const onSubmit = async (
        values: SupplierCreateParams & {
            attachments: File[] | Attachment[]
        }
    ) => {
        // Block submit if tax code exists
        if (codeExists) {
            setError('partner_tax_code', {
                type: 'manual',
                message: t('partner-tax-code-already-exists'),
            })
            return
        }

        let attachments: Attachment[] = []
        let attachmentsToDelete: number[] = []
        if (values?.attachments) {
            const keptAttachments = values.attachments.filter(
                (attachment) => !(attachment instanceof File)
            ) as Attachment[]
            const keptAttachmentIds = keptAttachments.map(
                (a) => a.attachment_id
            )

            const newFiles = values.attachments.filter(
                (attachment) => attachment instanceof File
            ) as File[]
            const attachmentUploadeds = await uploadAsync(newFiles)
            const uploadedAttachments = attachmentUploadeds
                ?.filter((attachment) => attachment.status === 'fulfilled')
                .map((attachment) => attachment.value) as Attachment[]

            attachments = [...keptAttachments, ...uploadedAttachments]

            const originalAttachmentIds =
                supplier?.attachments?.map((a) => a.attachment_id) || []
            attachmentsToDelete = originalAttachmentIds.filter(
                (id) => !keptAttachmentIds.includes(id)
            )
        }

        const { attachments: _, ...data } = values

        if (mode === 'update' && supplier) {
            await update({
                partnerID: supplier.partner_id,
                data,
                attachments,
                attachments_to_delete: attachmentsToDelete,
            })
            return
        }

        const response = await create({
            data,
            attachments,
        })
        if (response?.result) {
            onCreated?.(response.result)
        }
    }

    const onDelete = (file: File | Attachment) => {
        const isFile = file instanceof File
        if (isFile) {
            const attachments = getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return attachment.name !== file.name
                }
                return true
            }) as File[]
            setValue('attachments', newAttachments)
            return
        }
        if (isAttachment(file)) {
            const attachments = getValues('attachments')
            const newAttachments = attachments?.filter((attachment) => {
                if (attachment instanceof File) {
                    return true
                }
                return attachment.attachment_id !== file.attachment_id
            }) as Attachment[]
            setValue('attachments', newAttachments)
        }
    }

    const isLoading = isPending || updatePending

    // Render validation status icon
    const renderCodeStatus = () => {
        if (isCheckingCode) {
            return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        }
        if (codeExists === true) {
            return <XCircle className="w-4 h-4 text-red-500" />
        }
        if (codeExists === false) {
            return <CheckCircle2 className="w-4 h-4 text-green-500" />
        }
        return null
    }

    const statusOptions = [
        { value: 'active', label: t('active') },
        { value: 'inactive', label: t('inactive') },
        { value: 'suspended', label: t('suspended') },
    ]

    return (
        <Dialog
            title={t(`${mode}-supplier`)}
            onClose={onClose}
            open={open}
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-4 gap-4">
                    {/* Partner Name */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_name"
                            type="textfield"
                            label={t('partner_name')}
                            placeholder={t('partner_name')}
                            required
                        />
                    </div>

                    {/* Tax Code with validation */}
                    <div className="col-span-2">
                        <div className="relative">
                            <FormItem
                                control={control}
                                name="partner_tax_code"
                                type="textfield"
                                label={t('partner_tax_code')}
                                placeholder={t('partner_tax_code')}
                                required
                                onBlur={handleCheckTaxCode}
                                error={!!errors.partner_tax_code}
                                helperText={errors.partner_tax_code?.message}
                            />
                            <div className="absolute right-3 top-9">
                                {renderCodeStatus()}
                            </div>
                        </div>
                    </div>

                    {/* Email */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_email"
                            type="textfield"
                            label={t('partner_email')}
                            placeholder={t('partner_email')}
                        />
                    </div>

                    {/* Phone */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_phone"
                            type="textfield"
                            label={t('partner_phone')}
                            placeholder={t('partner_phone')}
                        />
                    </div>

                    {/* Status */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_status"
                            type="select"
                            label={t('status')}
                            placeholder={t('select-status')}
                            options={statusOptions}
                        />
                    </div>

                    {/* Address */}
                    <div className="col-span-4">
                        <FormItem
                            control={control}
                            name="partner_address"
                            type="textfield"
                            label={t('partner_address')}
                            placeholder={t('partner_address')}
                        />
                    </div>

                    {/* Representative */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_representative"
                            type="textfield"
                            label={t('partner_representative')}
                            placeholder={t('partner_representative')}
                        />
                    </div>

                    {/* Representative Phone */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_representative_phone"
                            type="textfield"
                            label={t('partner_representative_phone')}
                            placeholder={t('partner_representative_phone')}
                        />
                    </div>

                    {/* Bank Account */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_bank_account"
                            type="textfield"
                            label={t('partner_bank_account')}
                            placeholder={t('partner_bank_account')}
                        />
                    </div>

                    {/* Bank Name */}
                    <div className="col-span-2">
                        <FormItem
                            control={control}
                            name="partner_bank_name"
                            type="textfield"
                            label={t('partner_bank_name')}
                            placeholder={t('partner_bank_name')}
                        />
                    </div>

                    {/* Attachments */}
                    <div className="col-span-4">
                        <FormItem
                            control={control}
                            name="attachments"
                            type="files"
                            renderFileItem={(file: File | Attachment) =>
                                renderFileItem(file, onDelete)
                            }
                            label={t('attachments')}
                            placeholder={t('attachments')}
                        />
                    </div>

                    {/* Note */}
                    <div className="col-span-4">
                        <FormItem
                            control={control}
                            name="partner_note"
                            type="textarea"
                            label={t('partner_note')}
                            placeholder={t('partner_note')}
                        />
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 mt-4">
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={isLoading}
                        disabled={isLoading || codeExists === true}
                    >
                        {t(`${mode}-supplier`)}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default SupplierModal
